import { useState, useRef, useEffect } from 'react'
import { X, Plus } from 'lucide-react'

export default function Typeahead({
  label,
  placeholder = 'Search…',
  items = [],          // [{ id, label, sub }]
  value,               // selected id
  onChange,            // (id, item) => void
  required = false,
  className = '',
  onCreate,            // optional async (name) => { success, id, message } — offers "add <name>"
  createLabel = 'Add',
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const ref = useRef(null)

  const selected = items.find((i) => i.id === value)

  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const filtered = query.trim()
    ? items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()) ||
        (i.sub && i.sub.toLowerCase().includes(query.toLowerCase())))
    : items

  // Offer to create only when the typed name isn't already one of the options —
  // otherwise a near-match invites a duplicate of a record that already exists.
  const trimmed = query.trim()
  const exactMatch = items.some((i) => i.label.toLowerCase() === trimmed.toLowerCase())
  const canCreate = Boolean(onCreate) && trimmed.length > 0 && !exactMatch

  function handleSelect(item) {
    onChange(item.id, item)
    setQuery('')
    setOpen(false)
    setCreateError('')
  }

  function handleClear() {
    onChange('', null)
    setQuery('')
    setCreateError('')
  }

  async function handleCreate() {
    setCreating(true)
    setCreateError('')
    const result = await onCreate(trimmed)
    setCreating(false)
    if (!result?.success) {
      setCreateError(result?.message || 'Could not add that.')
      return
    }
    onChange(result.id, { id: result.id, label: trimmed })
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>}

      {selected ? (
        <div className="flex items-center justify-between px-3 py-2.5 border border-blue-400 rounded-xl bg-blue-50">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{selected.label}</p>
            {selected.sub && <p className="text-xs text-gray-500 truncate">{selected.sub}</p>}
          </div>
          <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            required={required}
          />
          {open && (filtered.length > 0 || canCreate || trimmed) && (
            <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={() => handleSelect(item)}
                  className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition border-b border-gray-50 last:border-0"
                >
                  <p className="text-sm font-medium text-gray-800">{item.label}</p>
                  {item.sub && <p className="text-xs text-gray-400">{item.sub}</p>}
                </button>
              ))}

              {filtered.length === 0 && trimmed && !canCreate && (
                <p className="px-3 py-3 text-sm text-gray-400">No results for &ldquo;{trimmed}&rdquo;</p>
              )}

              {canCreate && (
                <button
                  type="button"
                  disabled={creating}
                  onMouseDown={(e) => { e.preventDefault(); handleCreate() }}
                  className="w-full flex items-center gap-2 text-left px-3 py-2.5 border-t border-gray-100 hover:bg-blue-50 transition disabled:opacity-60"
                >
                  <Plus className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-blue-600 truncate">
                    {creating ? 'Adding…' : `${createLabel} “${trimmed}”`}
                  </span>
                </button>
              )}
            </div>
          )}
          {createError && <p className="mt-1 text-xs text-red-600">{createError}</p>}
        </>
      )}
    </div>
  )
}
